import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import ProjectDetailsHeader from '../../ProjectDetails/ProjectDetailsHeader';
import CrmGeneralSettings from './Sections/CrmGeneralSettings';
import { selectAdminProjectById } from '../../../redux/selectors/adminProjects';
import CrmPlatforms from './Sections/CrmPlatforms';
import CrmApproval from './Sections/CrmApproval';
import { PROJECT_STATUS } from '../../../interfaces/project';
import { editAdminAppStatus } from '../../../redux/admin/actions/AdminAppActions';
import CrmDataAttributes from './Sections/CrmDataAttributes';
import CrmPrivacyAndTerms from './Sections/CrmPrivacyAndTerms';
import CrmDescriptionAndPreview from './Sections/CrmDescriptionAndPreview';
import CrmRating from './Sections/CrmRating';
import { useAdminData } from '../../../helpers/customHooks';
import { fetchPermissionsForm } from '../../../services/FileUploadService';

const useStyles = makeStyles({
  container: {
    display: 'grid',
    gridGap: '32px',
  },
});

const CrmProjectOverviewPage: React.FC = () => {
  useAdminData();
  const params = useParams<{ projectId: string }>();
  const project = useSelector(selectAdminProjectById(params.projectId));
  const dispatch = useDispatch();
  const classes = useStyles();

  if (!project) return <Typography variant="h1">The project was not found.</Typography>;

  const { application } = project;

  const onChangeStatus = async (status: PROJECT_STATUS, reason?: string) => {
    if (!application.info.applicationId) return;

    dispatch(editAdminAppStatus(application.info.applicationId, status, reason));
  };

  const onFileDownload = async () => {
    if (!application.info?.applicationId || !application.info?.version) return;

    try {
      const blob = await fetchPermissionsForm(
        application.info.applicationId,
        application.info.version,
      );
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'transaction_request_form.xlsx';
      link.click();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className={classes.container}>
      <ProjectDetailsHeader project={application} />
      <CrmGeneralSettings project={project} />
      <CrmPlatforms application={application} />
      <CrmPrivacyAndTerms application={application} />
      <CrmRating application={application} />
      <CrmDescriptionAndPreview application={application} />
      <CrmDataAttributes
        onDownload={onFileDownload}
        hasPermissionsForm={application.info.hasPermissionsForm}
      />
      <CrmApproval onChangeStatus={onChangeStatus} />
    </div>
  );
};

export default CrmProjectOverviewPage;
